/**
 * The card shown when there are no findings.
 *
 * "No findings" used to render as "connected and alarms are being accepted"
 * whatever the scope. For a site reached directly over the internet there is
 * no tunnel handshake, path status or receiver health to check, so that card
 * was a green tick over nothing — while the same page showed no alerts in an
 * hour and every camera quiet for two days. The all-clear now says what was
 * actually confirmed, and when nothing was, it says that instead.
 */
import React from "react";
import { formatRelative } from "./formatters";
import {
  AlarmReceiver,
  AlertsReceived,
  CameraLiveness,
  Finding,
  GapResult,
  RefreshStatus,
  ScopeMode,
  SiteConnectionDetail,
  SiteToSiteConnection,
  WireGuardTunnel,
} from "../troubleshootingInterfaces";
import { sitePorts } from "./probePrefill";
import { primaryHost } from "./integrationEndpoints";
import { isPrivateAddress } from "./probeTargets";
import { useTroubleshootingStore } from "../troubleshootingStore/troubleshootingStore";
import { pluralize } from "./formatters";
import { getPeerHealth } from "./wireGuardHealth";
import { getArmedStatusPresentation } from "../../Sites/SitesPage/SitesPageUtils/armedStatusPresentation";
import {
  connectionKindForSite,
  useScopeLabel,
  useSelectionScope,
} from "./useScope";
import { siteFamily } from "./siteFamily";
import {
  readSavedLoginStatus,
  SAVED_LOGIN_EVIDENCE_MINUTES,
  savedLoginSupported,
  savedLoginSystemLabel,
} from "./savedLoginTest";

export interface AllClearInput {
  scope: ScopeMode;
  scopeLabel: string;
  /** Set when scope is one site. */
  siteDetail: SiteConnectionDetail | null;
  smtpSiteIds: number[];
  /** Tunnels in scope. */
  tunnels: WireGuardTunnel[];
  /** Every loaded path; narrowed here to the sites in scope. */
  paths: SiteToSiteConnection[];
  scopedSiteIds: number[];
  receivers: AlarmReceiver[] | null;
  alertsReceived: AlertsReceived | null;
  /** One site: per-camera "working now" signals (ENG-2046). */
  cameraLiveness?: CameraLiveness | null;
  /** Why the liveness read failed, when it did: the missing evidence is named. */
  livenessUnavailable?: string | null;
  /** One site: its last camera-list refresh, i.e. saved-login test (ENG-2065). */
  savedLogin?: { siteId: number; status: RefreshStatus | null } | null;
  nowMs?: number;
}

export const buildAllClear = (input: AllClearInput): Finding => {
  const nowMs = input.nowMs ?? Date.now();
  const scoped = new Set(input.scopedSiteIds);
  const healthyPeers = input.tunnels.filter(
    (tunnel) => getPeerHealth(tunnel, nowMs) === "healthy"
  ).length;
  const pathsUp = input.paths.filter(
    (path) =>
      path.kind !== "wireguard" &&
      path.status.status === "up" &&
      (input.scope === "all" || path.site_ids.some((id) => scoped.has(id)))
  ).length;
  const receiversListening = (input.receivers ?? []).filter(
    (receiver) =>
      receiver.health.listening === "listening" &&
      (input.scope === "all" || receiver.site_ids.some((id) => scoped.has(id)))
  ).length;
  const alerts = input.alertsReceived?.count ?? null;
  const camerasAlerted = input.alertsReceived?.camerasAlerted ?? 0;
  const alarmsFlowing = alerts != null && alerts > 0;
  const nowMsLive = input.nowMs ?? Date.now();
  const live =
    input.scope === "site" &&
    input.cameraLiveness &&
    input.siteDetail &&
    input.cameraLiveness.siteId === input.siteDetail.id
      ? input.cameraLiveness
      : null;
  const minutesSince = (iso: string | null | undefined) =>
    iso ? (nowMsLive - new Date(iso).getTime()) / 60_000 : null;
  const connectorAge = minutesSince(live?.connector?.lastSeenAt);
  const connectorFresh = connectorAge != null && connectorAge <= 10;
  const continuous = live ? live.cameras.filter((c) => c.continuous) : [];
  const videoFlowing =
    live != null &&
    continuous.length > 0 &&
    continuous.every((camera) => {
      const age = minutesSince(camera.lastFrameAt ?? camera.bandwidthUpdatedAt);
      return age != null && age <= 15 && camera.brokenFrame !== true;
    });
  const lastImage =
    live != null && live.signals.lastImage
      ? live.cameras
          .map((camera) => camera.lastImageReceivedAt)
          .filter((at): at is string => Boolean(at))
          .sort()
          .pop() ?? null
      : null;
  // A recent successful saved login (Test saved login, or any camera-list
  // refresh) proves Actuate reached the site's VMS and the login works.
  const loginOutcome =
    input.scope === "site" &&
    input.siteDetail &&
    input.savedLogin &&
    input.savedLogin.siteId === input.siteDetail.id
      ? readSavedLoginStatus(input.savedLogin.status)
      : null;
  const loginRecent =
    loginOutcome != null &&
    loginOutcome.state !== "running" &&
    (nowMsLive - loginOutcome.at * 1000) / 60_000 <=
      SAVED_LOGIN_EVIDENCE_MINUTES;
  const loginOk = loginRecent && loginOutcome?.state === "ok";
  const loginRefused = loginRecent && loginOutcome?.state === "wrong_login";
  const loginAt =
    loginOutcome && loginOutcome.state !== "running"
      ? new Date(loginOutcome.at * 1000).toISOString()
      : null;
  const system = savedLoginSystemLabel(input.siteDetail?.integration_type_name);
  const verified =
    alarmsFlowing ||
    loginOk ||
    connectorFresh ||
    videoFlowing ||
    healthyPeers > 0 ||
    pathsUp > 0 ||
    receiversListening > 0;

  const siteName =
    input.siteDetail?.name ||
    (input.scope === "site" ? input.scopeLabel : "This site");
  const subject =
    input.scope === "all"
      ? "All sites"
      : input.scope === "group"
      ? `Every site in ${input.scopeLabel}`
      : siteName;
  const verb = input.scope === "all" ? "are" : "is";

  const armed =
    input.siteDetail &&
    (input.siteDetail.armed != null || input.siteDetail.armed_status)
      ? getArmedStatusPresentation(
          input.siteDetail.armed_status,
          input.siteDetail.armed,
          input.siteDetail.active ?? true
        )
      : null;
  const evidence = [
    loginOk && loginAt
      ? `Its ${system} accepted our login with the saved credentials ${formatRelative(
          loginAt,
          nowMsLive
        )}.`
      : "",
    armed && (armed.tone === "armed" || armed.tone === "manual")
      ? `The site is ${armed.label.toLowerCase()}.`
      : "",
    alarmsFlowing
      ? `We processed ${
          input.alertsReceived?.truncated ? "at least " : ""
        }${pluralize(alerts ?? 0, "alert")} for this scope in the last hour${
          input.scope === "site"
            ? ", so cameras are reachable and alarms are flowing."
            : camerasAlerted > 0
            ? `, from ${pluralize(camerasAlerted, "camera")}.`
            : "."
        }`
      : "",
    videoFlowing && live
      ? `${
          continuous.length === 1
            ? "Its camera is"
            : `All ${continuous.length} cameras are`
        } delivering video.`
      : connectorFresh
      ? `The connector reported ${formatRelative(
          live?.connector?.lastSeenAt as string,
          nowMsLive
        )}.`
      : "",
    lastImage
      ? `The most recent camera image arrived ${formatRelative(
          lastImage,
          nowMsLive
        )}.`
      : "",
    !alarmsFlowing && (connectorFresh || videoFlowing)
      ? "No alerts in the last hour just means nothing moved: alerts only come from motion."
      : "",
    healthyPeers > 0
      ? `${pluralize(
          healthyPeers,
          "WireGuard peer"
        )} handshaked in the last 15 minutes.`
      : "",
    pathsUp > 0
      ? `${pluralize(
          pathsUp,
          "network path reports",
          "network paths report"
        )} up.`
      : "",
    receiversListening > 0
      ? `${pluralize(
          receiversListening,
          "alarm receiver is",
          "alarm receivers are"
        )} listening.`
      : "",
  ].filter(Boolean);

  const siteRef =
    input.scope === "site" && input.siteDetail
      ? { siteId: input.siteDetail.id, siteName: input.siteDetail.name }
      : {};
  if (verified) {
    // For a group or all sites, the evidence is aggregate: alerts from some
    // cameras, a few tunnels or receivers. That says nothing about each
    // site, so don't claim "every site is connected" (308 cameras alerting
    // stood in for 337 sites); say what was seen and how to check a site.
    const aggregate = input.scope !== "site";
    const where =
      input.scope === "all" ? "across all sites" : `in ${input.scopeLabel}`;
    const title = aggregate
      ? alarmsFlowing
        ? `Alarms are flowing ${where}`
        : `No checks are failing ${where}`
      : alarmsFlowing
      ? `${subject} ${verb} connected and alarms are flowing`
      : `${subject} ${verb} connected and no checks are failing`;
    const perSite = aggregate
      ? " We don't check each site's connection at this scope. Pick a site to check it."
      : "";
    return {
      id: "healthy",
      severity: "good",
      owner: "actuate",
      kind: "healthy",
      ...siteRef,
      title,
      since: null,
      body: `${evidence.join(
        " "
      )}${perSite} If you are still not seeing alarms, pick a symptom under “Start from a symptom” and we will point you at the most likely cause.`,
      steps: [],
      affectedSiteIds: [],
      metric: { value: "0", label: "open issues" },
      actions: ["copy"],
    };
  }

  // Nothing failed, but nothing covered this scope either. Plain sentences
  // for customers: no raw integration names, no endpoints, no jargon.
  const detail = input.siteDetail;
  const kind = connectionKindForSite(detail, input.smtpSiteIds);
  const oneSite = input.scope === "site" && detail != null;
  // What the connector dials first (management server for Milestone, the
  // vendor cloud for OpenEye OWS / the Nx relay), not raw server_ip, which
  // for some integrations holds an ID.
  const reachHost = detail ? (primaryHost(detail) || "").trim() : "";
  const isMilestone =
    oneSite && siteFamily(detail?.integration_type_name) === "milestone";
  const connection = !oneSite
    ? "No site here uses a tunnel or VPN that we track, so there is no connection status to check."
    : kind === "smtp"
    ? `${siteName} sends its images to us by email, so there is no connection of ours to check.`
    : !reachHost
    ? `We don't have an address for ${siteName}, so there is no connection status to check.`
    : isPrivateAddress(reachHost)
    ? `${siteName} is reached over a private network, usually a site-to-site VPN, and we don't track that VPN's status here yet.`
    : `${siteName} is reached over the internet at ${reachHost}, so there is no tunnel status to check.`;
  const receiverLine = isMilestone
    ? "Its alarms go to its Milestone event server. To test that connection, use Check path on the Alarm Receivers tab."
    : input.receivers == null
    ? "We can't see alarm receivers here yet."
    : oneSite
    ? "None of its alarm receivers reported that it is listening."
    : "";
  const livenessLine =
    oneSite && input.livenessUnavailable
      ? "We couldn't read its connector status here, so we can't tell whether the connector is running."
      : "";
  const quiet =
    alerts === 0
      ? oneSite
        ? "No alerts came in from it in the last hour."
        : "No alerts came in from these sites in the last hour."
      : alerts == null
      ? "We're still counting recent alerts."
      : "";
  const closing = `Nothing failed, but nothing confirmed ${
    oneSite ? "its" : "their"
  } cameras are reachable, so don't read this as everything working.`;
  const loginLine =
    loginRefused && loginAt
      ? `Its ${system} refused the saved username or password ${formatRelative(
          loginAt,
          nowMsLive
        )}; update the login on the site page.`
      : "";
  const canTestLogin = oneSite && detail != null && savedLoginSupported(detail);
  const body = [
    connection,
    receiverLine,
    livenessLine,
    loginLine,
    quiet,
    closing,
  ]
    .filter(Boolean)
    .join(" ");
  return {
    id: "healthy",
    severity: "warn",
    owner: "shared",
    kind: "healthy",
    ...siteRef,
    title: `We could not confirm ${
      input.scope === "all"
        ? "your sites are"
        : input.scope === "group"
        ? `the sites in ${input.scopeLabel} are`
        : `${siteName} is`
    } connected`,
    since: null,
    body,
    steps:
      input.scope === "site" && reachHost
        ? [
            `Use Check reachability to test whether ${reachHost} answers from Actuate.`,
            isMilestone
              ? "Then use Check path on the Alarm Receivers tab to test the connection our alarms take."
              : "Then send a test alarm from the Alarm Receivers tab to prove alarms get through.",
            ...(canTestLogin
              ? [
                  `Use Test saved login on the Connections tab to log in to its ${system} the way the connector does.`,
                ]
              : []),
          ]
        : [],
    affectedSiteIds: [],
    metric: { value: "0", label: "checks confirmed" },
    actions:
      input.scope === "site" && reachHost ? ["probe-host", "copy"] : ["copy"],
    probeHost: input.scope === "site" && reachHost ? reachHost : undefined,
    probePorts:
      input.scope === "site" && reachHost && detail
        ? sitePorts(detail)
        : undefined,
    siteId: input.scope === "site" && detail ? detail.id : undefined,
  };
};

/** A failed or missing liveness read, in words; null when it loaded or is still loading. */
export const livenessProblem = (
  result: GapResult<CameraLiveness> | null | undefined
): string | null => {
  if (!result || result.status === "ok") return null;
  return result.status === "unavailable"
    ? `${result.endpoint} is not deployed in this environment yet`
    : `${result.endpoint} failed: ${result.message}`;
};

/** The all-clear for the current scope, from the store. */
export const useAllClear = (): Finding => {
  const {
    scope,
    siteDetail,
    smtpSiteIds,
    siteToSite,
    receivers,
    alertsReceived,
    cameraLiveness,
    savedLoginStatus,
  } = useTroubleshootingStore();
  const { tunnels, sites: scopedSites } = useSelectionScope();
  const scopeLabel = useScopeLabel();
  return React.useMemo(
    () =>
      buildAllClear({
        scope,
        scopeLabel,
        siteDetail: scope === "site" ? siteDetail : null,
        smtpSiteIds,
        tunnels,
        paths: siteToSite?.status === "ok" ? siteToSite.data : [],
        scopedSiteIds: scopedSites.map((site) => site.id),
        receivers: receivers?.status === "ok" ? receivers.data : null,
        alertsReceived:
          alertsReceived?.status === "ok" ? alertsReceived.data : null,
        cameraLiveness:
          cameraLiveness?.status === "ok" ? cameraLiveness.data : null,
        livenessUnavailable: livenessProblem(cameraLiveness),
        savedLogin: savedLoginStatus,
      }),
    [
      scope,
      scopeLabel,
      siteDetail,
      smtpSiteIds,
      tunnels,
      siteToSite,
      scopedSites,
      receivers,
      alertsReceived,
      cameraLiveness,
      savedLoginStatus,
    ]
  );
};
